import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Quiz as QuizIcon,
  NavigateNext as NextIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { updateQuizScore, updateQuizResult } from '../../store/progressSlice';
import { QuizQuestion } from '../../types';

interface QuizSystemProps {
  courseId: string;
  moduleId: string;
}

interface QuizAnswer {
  questionId: string;
  selectedAnswer: number;
}

const QuizSystem: React.FC<QuizSystemProps> = ({ courseId, moduleId }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const course = useAppSelector(state => state.courses[courseId]);
  const progress = useAppSelector(state => state.progress.courseProgress[courseId]);

  const module = course?.modules.find(m => m.id === moduleId);
  const quiz = module?.quiz;

  // Generate random questions on component mount
  const [selectedQuestions, setSelectedQuestions] = useState<QuizQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswer[]>([]);
  const [, setIsCompleted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);

  // Initialize random question selection
  useEffect(() => {
    if (quiz) {
      if (quiz.questionBank && quiz.questionsPerQuiz) {
        // Randomly select questions from the question bank
        const shuffled = [...quiz.questionBank].sort(() => 0.5 - Math.random());
        const selected = shuffled.slice(0, quiz.questionsPerQuiz);
        setSelectedQuestions(selected);
      } else {
        // Use the default questions if no question bank
        setSelectedQuestions(quiz.questions);
      }
    }
  }, [quiz]);

  const previousScore = quiz?.id && progress?.quizScores ? progress.quizScores[quiz.id] : undefined;
  const lastResult = quiz?.id && progress?.quizResults ? progress.quizResults[quiz.id] : undefined;

  if (!course || !module || !quiz || selectedQuestions.length === 0) {
    return (
      <Container maxWidth='lg' sx={{ py: 4 }}>
        <Typography variant='h4'>Quiz not found</Typography>
      </Container>
    );
  }

  const currentQuestion = selectedQuestions[currentQuestionIndex];
  const currentAnswer = answers.find(a => a.questionId === currentQuestion.id);
  const isLastQuestion = currentQuestionIndex === selectedQuestions.length - 1;

  const handleAnswerChange = (selectedAnswer: number) => {
    const newAnswers = answers.filter(a => a.questionId !== currentQuestion.id);
    newAnswers.push({
      questionId: currentQuestion.id,
      selectedAnswer,
    });
    setAnswers(newAnswers);
  };

  const handleNext = () => {
    if (isLastQuestion) {
      completeQuiz();
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const completeQuiz = () => {
    let correctAnswers = 0;
    selectedQuestions.forEach(question => {
      const userAnswer = answers.find(a => a.questionId === question.id);
      if (userAnswer && userAnswer.selectedAnswer === question.correctAnswer) {
        correctAnswers++;
      }
    });

    const finalScore = correctAnswers;
    setScore(finalScore);
    setIsCompleted(true);
    setShowResults(true);

    // Create answer map for storage
    const answerMap: Record<string, number> = {};
    answers.forEach(answer => {
      answerMap[answer.questionId] = answer.selectedAnswer;
    });

    // Save detailed quiz result
    const quizResult = {
      score: finalScore,
      totalQuestions: selectedQuestions.length,
      passed: passed,
      completedAt: new Date().toISOString(),
      answers: answerMap,
      attemptNumber: 1, // This will be updated by the QuizService
    };

    dispatch(
      updateQuizResult({
        courseId,
        quizId: quiz.id,
        result: quizResult,
      })
    );

    // Also save score for backward compatibility
    dispatch(
      updateQuizScore({
        courseId,
        quizId: quiz.id,
        score: finalScore,
      })
    );
  };

  const getQuestionResult = (questionId: string) => {
    const question = selectedQuestions.find(q => q.id === questionId);
    const userAnswer = answers.find(a => a.questionId === questionId);

    if (!question || !userAnswer) return null;

    return {
      correct: userAnswer.selectedAnswer === question.correctAnswer,
      userAnswer: userAnswer.selectedAnswer,
      correctAnswer: question.correctAnswer,
      explanation: question.explanation,
    };
  };

  const passed = score >= quiz.passingScore;
  const progressPercentage = ((currentQuestionIndex + 1) / selectedQuestions.length) * 100;

  if (showResults) {
    return (
      <Container maxWidth='lg' sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <QuizIcon sx={{ fontSize: 64, color: passed ? 'success.main' : 'error.main', mb: 2 }} />
          <Typography variant='h3' component='h1' gutterBottom>
            Quiz Complete!
          </Typography>

          <Typography variant='h4' gutterBottom>
            Score: {score} / {selectedQuestions.length}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Chip
              label={passed ? 'Passed' : 'Failed'}
              color={passed ? 'success' : 'error'}
              size='medium'
            />
          </Box>

          <Typography variant='body1' color='text.secondary'>
            You needed {quiz.passingScore} correct answers to pass.
          </Typography>

          {previousScore !== undefined && previousScore !== score && (
            <Alert severity={score > previousScore ? 'success' : 'info'} sx={{ mt: 2, mb: 2 }}>
              {score > previousScore
                ? `Improved! Previous best: ${previousScore}/${selectedQuestions.length}`
                : `Previous best: ${previousScore}/${selectedQuestions.length}`}
            </Alert>
          )}
        </Box>

        {/* Results Breakdown */}
        <Typography variant='h5' gutterBottom sx={{ mt: 4 }}>
          Question Review
        </Typography>

        {selectedQuestions.map((question, index) => {
          const result = getQuestionResult(question.id);

          return (
            <Card key={question.id} sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant='h6' sx={{ flexGrow: 1 }}>
                    Question {index + 1}
                  </Typography>
                  {result?.correct ? <CheckIcon color='success' /> : <CancelIcon color='error' />}
                </Box>

                <Typography variant='body1' paragraph>
                  {question.question}
                </Typography>

                <Box sx={{ pl: 2 }}>
                  {question.choices.map((choice: string, choiceIndex: number) => (
                    <Typography
                      key={choiceIndex}
                      variant='body2'
                      sx={{
                        mb: 0.5,
                        color:
                          choiceIndex === question.correctAnswer
                            ? 'success.main'
                            : choiceIndex === result?.userAnswer
                              ? 'error.main'
                              : 'text.primary',
                        fontWeight:
                          choiceIndex === question.correctAnswer ||
                          choiceIndex === result?.userAnswer
                            ? 'bold'
                            : 'normal',
                      }}
                    >
                      {choiceIndex === question.correctAnswer && '✓ '}
                      {choiceIndex === result?.userAnswer &&
                        choiceIndex !== question.correctAnswer &&
                        '✗ '}
                      {choice}
                    </Typography>
                  ))}
                </Box>

                {result?.explanation && (
                  <Alert severity='info' sx={{ mt: 2 }}>
                    <strong>Explanation:</strong> {result.explanation}
                  </Alert>
                )}
              </CardContent>
            </Card>
          );
        })}

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            variant='contained'
            onClick={() => navigate(`/courses/${courseId}/modules/${moduleId}`)}
            size='large'
          >
            Return to Module
          </Button>

          {!passed && (
            <Button
              variant='outlined'
              onClick={() => window.location.reload()}
              sx={{ ml: 2 }}
              size='large'
            >
              Retake Quiz
            </Button>
          )}
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth='lg' sx={{ py: 4 }}>
      {/* Quiz Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant='h3' component='h1'>
            {quiz.title}
          </Typography>
          <Button
            variant='outlined'
            startIcon={<BackIcon />}
            onClick={() => navigate(`/courses/${courseId}/modules/${moduleId}`)}
            sx={{ ml: 2 }}
          >
            Exit Quiz
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Chip label={`${selectedQuestions.length} questions`} />
          <Chip label={`Pass: ${quiz.passingScore}/${selectedQuestions.length}`} />
          {lastResult && (
            <Chip
              label={`Last: ${lastResult.score}/${lastResult.totalQuestions} ${lastResult.passed ? '✓' : '✗'} (${new Date(lastResult.completedAt).toLocaleDateString()})`}
              color={lastResult.passed ? 'success' : 'error'}
            />
          )}
          {previousScore !== undefined && (
            <Chip label={`Best: ${previousScore}/${selectedQuestions.length}`} color='primary' />
          )}
        </Box>

        {/* Progress Bar */}
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant='body2' sx={{ flexGrow: 1 }}>
              Question {currentQuestionIndex + 1} of {selectedQuestions.length}
            </Typography>
            <Typography variant='body2'>{Math.round(progressPercentage)}% Complete</Typography>
          </Box>
          <LinearProgress
            variant='determinate'
            value={progressPercentage}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Paper>
      </Box>

      {/* Current Question */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant='h5' component='h2' gutterBottom>
          Question {currentQuestionIndex + 1}
        </Typography>

        <Typography variant='body1' paragraph sx={{ fontSize: '1.1rem' }}>
          {currentQuestion.question}
        </Typography>

        <FormControl component='fieldset' fullWidth>
          <RadioGroup
            value={currentAnswer?.selectedAnswer ?? ''}
            onChange={e => handleAnswerChange(parseInt(e.target.value))}
          >
            {currentQuestion.choices.map((choice: string, index: number) => (
              <FormControlLabel
                key={index}
                value={index}
                control={<Radio />}
                label={choice}
                sx={{ mb: 1, ml: 0 }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Paper>

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button variant='outlined' onClick={handlePrevious} disabled={currentQuestionIndex === 0}>
          Previous
        </Button>

        <Typography variant='body2' color='text.secondary'>
          {answers.length} of {selectedQuestions.length} questions answered
        </Typography>

        <Button
          variant='contained'
          onClick={handleNext}
          disabled={!currentAnswer}
          endIcon={isLastQuestion ? undefined : <NextIcon />}
        >
          {isLastQuestion ? 'Complete Quiz' : 'Next Question'}
        </Button>
      </Box>

      {/* Question Overview */}
      <Paper sx={{ mt: 4, p: 3 }}>
        <Typography variant='h6' gutterBottom>
          Question Overview
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {selectedQuestions.map((question, index) => {
            const hasAnswer = answers.some(a => a.questionId === question.id);
            const isCurrent = index === currentQuestionIndex;

            return (
              <Button
                key={question.id}
                variant={isCurrent ? 'contained' : hasAnswer ? 'outlined' : 'text'}
                onClick={() => setCurrentQuestionIndex(index)}
                sx={{
                  minWidth: 40,
                  height: 40,
                  borderRadius: '50%',
                  p: 0,
                }}
              >
                {index + 1}
              </Button>
            );
          })}
        </Box>
      </Paper>
    </Container>
  );
};

export default QuizSystem;
